import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { format } from "date-fns";
import { MapPin, X, ArrowRight } from "./icons";
import { API } from "@/config";

import { TripSetupHeroSVG } from "./TripSetupHeroSVG";
import { LocationPicker, isLocationSet, isDisneyLocation } from "./LocationPicker";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { DifferentPickupSection } from "./DifferentPickupSection";
import { DatesSection } from "./DatesSection";
import { TimeWindowsSection } from "./TimeWindowsSection";
import { DisneyAvailabilitySection } from "./DisneyAvailabilitySection";

/* ==================== Trip Setup Modal ====================
 * Full-screen modal captured above the catalog. Composes six focused
 * sub-components (Hero SVG, Location picker × 2, Dates, Times, Disney
 * confirmation). Local state lives here so the sub-components stay dumb
 * and easy to reason about.
 */
export const TripSetupModal = ({
  open, onClose, onConfirm,
  initialRange, initialIsOffProperty, initialZoneId, initialResort, initialAddress, initialAddrCheck,
  initialDeliverySlot, initialPickupSlot, initialDisneyAvailability,
  initialDifferentPickup, initialPickupIsOffProperty, initialPickupZoneId, initialPickupResort, initialPickupAddress, initialPickupAddrCheck,
  zones, timeSlots,
}) => {
  // -------- Delivery location state --------
  const [range, setRange] = useState(initialRange);
  const [isOffProperty, setIsOffProperty] = useState(initialIsOffProperty);
  const [zoneId, setZoneId] = useState(initialZoneId);
  const [resort, setResort] = useState(initialResort);
  const [address, setAddress] = useState(initialAddress);
  const [addrCheck, setAddrCheck] = useState(initialAddrCheck);
  const [deliverySlot, setDeliverySlot] = useState(initialDeliverySlot || "");
  const [pickupSlot, setPickupSlot] = useState(initialPickupSlot || "");
  const [disneyAvailability, setDisneyAvailability] = useState(!!initialDisneyAvailability);
  const [calendarOpen, setCalendarOpen] = useState(!(initialRange?.from && initialRange?.to));

  // -------- Optional split-pickup location state --------
  const [differentPickup, setDifferentPickup] = useState(!!initialDifferentPickup);
  const [pickupIsOffProperty, setPickupIsOffProperty] = useState(!!initialPickupIsOffProperty);
  const [pickupZoneId, setPickupZoneId] = useState(initialPickupZoneId || "");
  const [pickupResort, setPickupResort] = useState(initialPickupResort || "");
  const [pickupAddress, setPickupAddress] = useState(initialPickupAddress || "");
  const [pickupAddrCheck, setPickupAddrCheck] = useState(initialPickupAddrCheck || null);

  // -------- Admin booking settings — blackout dates + surge pricing --------
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    if (!open) return;
    let alive = true;
    axios.get(`${API}/settings`).then((r) => { if (alive) setSettings(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [open]);
  const blackoutDates = useMemo(() => {
    if (!settings?.blackout_dates) return [];
    return settings.blackout_dates.map((s) => {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d);
    });
  }, [settings]);
  const activeSurge = useMemo(() => {
    if (!settings?.surge_windows?.length || !range?.from || !range?.to) return null;
    const s = format(range.from, "yyyy-MM-dd");
    const e = format(range.to, "yyyy-MM-dd");
    return settings.surge_windows.find((w) => s <= w.end_date && w.start_date <= e) || null;
  }, [settings, range]);

  // -------- Reset local state whenever the modal re-opens --------
  useEffect(() => {
    if (open) {
      setRange(initialRange);
      setIsOffProperty(initialIsOffProperty);
      setZoneId(initialZoneId || (initialIsOffProperty ? "" : (zones.find((z) => z.id === "wdw-core")?.id || "")));
      setResort(initialResort);
      setAddress(initialAddress);
      setAddrCheck(initialAddrCheck);
      setDeliverySlot(initialDeliverySlot || "");
      setPickupSlot(initialPickupSlot || "");
      setDisneyAvailability(!!initialDisneyAvailability);
      setCalendarOpen(!(initialRange?.from && initialRange?.to));
      setDifferentPickup(!!initialDifferentPickup);
      setPickupIsOffProperty(!!initialPickupIsOffProperty);
      setPickupZoneId(initialPickupZoneId || "");
      setPickupResort(initialPickupResort || "");
      setPickupAddress(initialPickupAddress || "");
      setPickupAddrCheck(initialPickupAddrCheck || null);
    }
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (range?.from && range?.to) setCalendarOpen(false);
  }, [range?.from, range?.to]);

  if (!open) return null;

  // -------- Derived flags used to gate/enable each step --------
  const deliveryState = { isOffProperty, zoneId, resort, address, addrCheck };
  const pickupState = { isOffProperty: pickupIsOffProperty, zoneId: pickupZoneId, resort: pickupResort, address: pickupAddress, addrCheck: pickupAddrCheck };
  const destinationSet = isLocationSet(deliveryState, zones);
  const isDisney = isDisneyLocation(deliveryState, zones);
  const isPickupDisney = differentPickup && isDisneyLocation(pickupState, zones);
  const anyDisney = isDisney || isPickupDisney;
  const datesSet = !!(range?.from && range?.to);

  // Same-day rule: hide delivery windows within the configured lead-time (from admin Booking Settings).
  // Falls back to 6 hours while settings are loading so the UI doesn't flash.
  const sameDayLeadHours = settings?.last_minute_threshold_hours ?? 6;
  const flHourNow = (() => {
    try {
      const parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        hour: "numeric", hour12: false, minute: "2-digit",
      }).formatToParts(new Date());
      const h = Number(parts.find((p) => p.type === "hour")?.value || 0);
      const m = Number(parts.find((p) => p.type === "minute")?.value || 0);
      return h + m / 60;
    } catch { return 0; }
  })();
  const isSameDayDelivery = range?.from && format(range.from, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const deliverySlotsFiltered = (timeSlots || []).filter((s) => {
    if (!isSameDayDelivery) return true;
    const start = Number(String(s.id).split("-")[0] || 0);
    return start >= flHourNow + sameDayLeadHours;
  });

  // Show a "not guaranteed" warning when the selected delivery slot is within 24 hours.
  // Covers same-day bookings AND next-day slots that start before the current FL hour
  // (e.g. booking at 3pm for 10am tomorrow = 19 hours away).
  const isWithin24HoursSelected = (() => {
    if (!range?.from || !deliverySlot) return false;
    if (isSameDayDelivery) return true;
    const startHour = Number(String(deliverySlot).split("-")[0] || 0);
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (format(range.from, "yyyy-MM-dd") === format(tomorrow, "yyyy-MM-dd") && startHour < flHourNow) return true;
    return false;
  })();

  // Show an "off-hours" warning when the ORDER IS PLACED between 9 PM – 7 AM FL time.
  // The customer should know we may be sleeping and won't confirm until morning.
  const isOffHoursBooking = Math.floor(flHourNow) >= 21 || Math.floor(flHourNow) < 7;

  // -------- Gate for the "Continue to Gear" button --------
  const canConfirm = (() => {
    if (!datesSet) return false;
    if (!destinationSet) return false;
    if (!deliverySlot || !pickupSlot) return false;
    if (differentPickup && !isLocationSet(pickupState, zones)) return false;
    if (anyDisney && !disneyAvailability) return false;
    return true;
  })();

  const confirm = () => {
    if (!canConfirm) return;
    onConfirm({
      range, isOffProperty, zoneId, resort, address, addrCheck,
      deliverySlot, pickupSlot, disneyAvailability,
      differentPickup,
      pickupIsOffProperty: differentPickup ? pickupIsOffProperty : false,
      pickupZoneId: differentPickup ? pickupZoneId : "",
      pickupResort: differentPickup ? pickupResort : "",
      pickupAddress: differentPickup ? pickupAddress : "",
      pickupAddrCheck: differentPickup ? pickupAddrCheck : null,
    });
  };

  const removePickup = () => {
    setDifferentPickup(false);
    setPickupIsOffProperty(false); setPickupZoneId(""); setPickupResort(""); setPickupAddress(""); setPickupAddrCheck(null);
  };

  return createPortal(
    <div
      className="fixed inset-0 flex items-start md:items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: "rgba(4, 10, 15, 0.97)", zIndex: 9999 }}
      onClick={onClose}
      data-testid="trip-setup-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[92vh] overflow-y-auto my-4 p-6 md:p-8 relative rounded-2xl border border-[#FFD166]/30 shadow-[0_25px_80px_-10px_rgba(0,0,0,0.9)]"
        style={{ backgroundColor: "#0A1A26", position: "relative", zIndex: 10000 }}
        onClick={(e) => e.stopPropagation()}
      >
        <TripSetupHeroSVG />

        <button onClick={onClose} className="absolute top-4 right-4 text-[#B9C8D5] hover:text-[#FFD166] p-2 z-20" data-testid="trip-setup-close" aria-label="Close">
          <X className="w-5 h-5" />
        </button>

        <div className="relative z-10">
          <p className="eyebrow text-[#FFD166]">Where &amp; When</p>
          <h3 className="font-serif text-3xl md:text-4xl mt-2 text-[#F6F1E4] leading-tight">
            Let&rsquo;s set the stage for your magic ✨
          </h3>
          <p className="text-[#B9C8D5] mt-2 text-sm">
            Tell us <em>where</em>, <em>when</em>, and pick your 1-hour delivery + pickup windows — then we&rsquo;ll show you in-stock gear.
          </p>
        </div>

        {/* ---------- STEP 1: DESTINATION ---------- */}
        <div className="mt-6">
          <label className="eyebrow flex items-center gap-2 mb-2">
            <MapPin className="w-3.5 h-3.5 text-[#FFD166]" /> 1 · Delivery address
            {destinationSet && <span className="text-emerald-400 text-[11px]" data-testid="dest-step-check">✓</span>}
          </label>
          <ErrorBoundary name="delivery-address" title="We hit a snag checking that address." message="Try selecting an address from the dropdown suggestions.">
          <LocationPicker
            state={deliveryState}
            setIsOffProperty={(v) => { setIsOffProperty(v); if (!v) setDisneyAvailability(false); }}
            setZoneId={setZoneId}
            setResort={setResort}
            setAddress={setAddress}
            setAddrCheck={setAddrCheck}
            zones={zones}
            testidPrefix="trip-setup"
            verb="deliver"
            labels={{
              disneyPlaceholder: "Pick your Disney resort…",
              areaPlaceholder: "Choose your area…",
              addressPlaceholder: "Full address (street, city, state, ZIP)",
              offsiteResortPlaceholder: "Pick your resort…",
              successAddress: "✓ Great — we deliver here!",
              failAddress: "We don't deliver to that address.",
            }}
          />
          </ErrorBoundary>
        </div>

        {/* ---------- STEP 1.5: DIFFERENT PICKUP LOCATION (opt-in) ---------- */}
        {destinationSet && (
          <DifferentPickupSection
            differentPickup={differentPickup}
            onToggle={() => setDifferentPickup(true)}
            onRemove={removePickup}
            pickupState={pickupState}
            setPickupIsOffProperty={setPickupIsOffProperty}
            setPickupZoneId={setPickupZoneId}
            setPickupResort={setPickupResort}
            setPickupAddress={setPickupAddress}
            setPickupAddrCheck={setPickupAddrCheck}
            zones={zones}
          />
        )}

        {/* ---------- STEP 2: DATES ---------- */}
        <DatesSection
          destinationSet={destinationSet}
          range={range}
          onRangeChange={setRange}
          blackoutDates={blackoutDates}
          activeSurge={activeSurge}
          calendarOpen={calendarOpen}
          onOpenCalendar={() => setCalendarOpen(true)}
        />

        {/* ---------- STEP 3: TIME WINDOWS ---------- */}
        {destinationSet && datesSet && (
          <TimeWindowsSection
            range={range}
            deliverySlot={deliverySlot}
            onDeliverySlotChange={setDeliverySlot}
            pickupSlot={pickupSlot}
            onPickupSlotChange={setPickupSlot}
            deliverySlotsFiltered={deliverySlotsFiltered}
            pickupSlots={timeSlots}
            isSameDayDelivery={isSameDayDelivery}
            sameDayLeadHours={sameDayLeadHours}
            isWithin24HoursSelected={isWithin24HoursSelected}
            isOffHoursBooking={isOffHoursBooking}
          />
        )}

        {/* ---------- STEP 4: DISNEY AVAILABILITY ---------- */}
        {destinationSet && datesSet && anyDisney && deliverySlot && (
          <DisneyAvailabilitySection
            isDisney={isDisney}
            isPickupDisney={isPickupDisney}
            samePickup={!differentPickup}
            resort={resort}
            pickupResort={pickupResort}
            disneyAvailability={disneyAvailability}
            onChange={setDisneyAvailability}
          />
        )}

        {/* ---------- CONTINUE ---------- */}
        {destinationSet && datesSet && (
          <button
            type="button"
            onClick={confirm}
            disabled={!canConfirm}
            className="btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="trip-setup-confirm"
          >
            Continue to Gear <ArrowRight className="inline w-4 h-4 ml-1" />
          </button>
        )}
        <p className="text-[11px] text-[#B9C8D5]/70 mt-3 text-center">
          You can change these anytime before checkout.
        </p>
      </div>
    </div>,
    document.body
  );
};

export default TripSetupModal;
